<template>
  <div class="standings-page">
    <div ref="standingsDropdownRef" class="dropdown">
      <van-sticky>
        <van-dropdown-menu active-color="#337ECC">
          <van-dropdown-item
            v-model="groupValue"
            :options="groupOptions"
            @change="loadStages"
          />
          <van-dropdown-item
            v-model="stageValue"
            :options="stageOptions"
            @change="onStageChange"
          />
        </van-dropdown-menu>
      </van-sticky>
    </div>

    <main class="standings-body" :style="bodyMinHeightStyle">
      <div v-if="bodyLoading" class="state-tip">
        <van-loading size="24px" vertical>加载中...</van-loading>
      </div>
      <template v-else-if="canShowContent">
        <div v-if="ruleType === '淘汰赛'" class="knockout-wrap">
          <KnockoutStandings :rounds="rounds" />
        </div>
        <div v-else-if="ruleType === '自定义'" class="custom-tip">
          自定义阶段暂无数据展示
        </div>
        <template v-else>
          <section
            v-for="(item, i) in list"
            :key="i"
            class="group-block"
          >
           <GroupData :data="item" />
          </section>
          <div v-if="list.length === 0" class="state-tip">
            <van-empty description="暂无积分榜数据" />
          </div>
        </template>
        <p class="match-note">赛事数据统计由组委会提供</p>
      </template>
    </main>
  </div>
</template>

<script>
import Vue from 'vue'
import { DropdownMenu, DropdownItem, Sticky, Loading, Empty } from 'vant'
Vue.use(DropdownMenu).use(DropdownItem).use(Sticky).use(Loading).use(Empty)
import GroupData from './components/group-data.vue'
import KnockoutStandings from './components/knockout-standings.vue'
import {
  getBMMatchList2,
  getBMStageGamesByBMMatchId,
  getBMTeamScores1,
  getBMMatchStageGameList,
} from '@/api/match'
import { QNImageUtil } from '@/utils'

function normalizeTeamScoreRow(score, index) {
  return {
    id: score.id != null ? score.id : `row-${index}`,
    rank: score.rankInGroup != null ? score.rankInGroup : index + 1,
    badge: score.badge,
    name: score.teamAlias || score.name || '',
    totalRound: score.totalRound,
    winRound: score.winRound,
    loseRound: score.loseRound,
    tieRound: score.tieRound,
    totalScore: score.totalScore,
    goalCount: score.goalCount,
    concededCount: score.concededCount,
    avgWinScorePerGame: score.avgWinScorePerGame,
    avgConcededScorePerGame: score.avgConcededScorePerGame,
    netWinGameStageCount: score.netWinGameStageCount,
    netWinScoreCount: score.netWinScoreCount,
    cValue: score.cValue,
    zValue: score.zValue,
    winByShootoutRound: score.winByShootoutRound,
    loseByShootoutRound: score.loseByShootoutRound,
  }
}

export default {
  components: { KnockoutStandings, GroupData },
  data() {
    return {
      unionMatchId: '',
      groupValue: '',
      groupOptions: [],
      stageValue: '',
      stageOptions: [],
      stagesRaw: [],
      match: {},
      ruleType: '',
      list: [],
      rounds: [],
      filtersReady: false,
      bodyLoading: false,
      bodyMinHeightPx: '',
    }
  },
  computed: {
    bodyMinHeightStyle() {
      return this.bodyMinHeightPx ? { minHeight: this.bodyMinHeightPx } : {}
    },
    canShowContent() {
      return this.filtersReady && !!this.groupValue
    },
  },
  mounted() {
    this.unionMatchId = this.$route.params.unionMatchId
    this.loadGroups()
    this.$nextTick(() => {
      this.updateBodyMinHeight()
      window.addEventListener('resize', this.updateBodyMinHeight)
      window.addEventListener('orientationchange', this.onOrientationChange)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateBodyMinHeight)
    window.removeEventListener('orientationchange', this.onOrientationChange)
  },
  methods: {
    updateBodyMinHeight() {
      this.$nextTick(() => {
        const el = this.$refs.standingsDropdownRef
        if (!el || typeof el.getBoundingClientRect !== 'function') return
        const rect = el.getBoundingClientRect()
        const vh = window.innerHeight || document.documentElement.clientHeight
        const next = `${Math.max(280, Math.floor(vh - rect.bottom - 12))}px`
        if (next !== this.bodyMinHeightPx) this.bodyMinHeightPx = next
      })
    },
    onOrientationChange() {
      setTimeout(() => this.updateBodyMinHeight(), 200)
    },

    loadGroups() {
      getBMMatchList2({ unionMatchId: this.unionMatchId }).then(res => {
        const list = (res && res.data && res.data.list) || []
        this.groupOptions = list.map(i => ({ text: i.group, value: i.id }))
        if (list.length === 0) {
          this.groupValue = ''
          this.stageOptions = []
          this.stageValue = ''
          this.filtersReady = true
          return
        }
        this.groupValue = list[0].id
        this.loadStages()
      }).catch(() => {
        this.groupOptions = []
        this.groupValue = ''
        this.filtersReady = true
      })
    },

    loadStages() {
      this.stageOptions = []
      this.stageValue = ''
      this.stagesRaw = []
      this.filtersReady = false
      if (!this.groupValue) {
        this.filtersReady = true
        return
      }

      getBMStageGamesByBMMatchId({
        unionMatchId: this.unionMatchId,
        matchId: this.groupValue,
      }).then(res => {
        const data = (res && res.data) || {}
        this.match = data.match || {}
        const unionMatch = data.unionMatch || {}
        const nameStr = this.match.chineseName || ''
        if (nameStr) {
          document.title = (nameStr.indexOf('_') >= 0 ? nameStr.split('_')[1] : nameStr) + '-积分榜'
          this.wxShare({
            title: (nameStr.indexOf('_') >= 0 ? nameStr.split('_')[0] : nameStr) + this.getPromotion(unionMatch.beginDate, unionMatch.endDate),
            abstract: (nameStr.indexOf('_') >= 0 ? nameStr.split('_')[1] : nameStr) + '/积分榜',
            image: QNImageUtil.imageView(this.match.icon),
          })
        }

        const stages = data.stages || []
        this.stagesRaw = stages
        this.stageOptions = stages.map(s => ({
          text: s.stageName != null ? s.stageName : String(s.id),
          value: s.id,
        }))
        if (stages.length > 0) {
          this.stageValue = stages[0].id
        }
        this.filtersReady = true
        this.$nextTick(() => {
          this.syncRuleTypeFromStage()
          this.refreshByRuleType()
        })
      }).catch(() => {
        this.stageOptions = []
        this.filtersReady = true
        this.$nextTick(() => {
          this.syncRuleTypeFromStage()
          this.refreshByRuleType()
        })
      })
    },

    onStageChange() {
      this.syncRuleTypeFromStage()
      this.refreshByRuleType()
    },

    syncRuleTypeFromStage() {
      const stages = this.stagesRaw || []
      if (stages.length === 0) {
        this.ruleType = (this.match && this.match.ruleType) || ''
        return
      }
      const st = stages.find(s => String(s.id) === String(this.stageValue))
      this.ruleType = (st && st.ruleType) || (this.match && this.match.ruleType) || ''
    },

    getPromotion(createDate, endDate) {
      if (!createDate || !endDate) return ''
      const now = Date.now()
      const start = new Date(createDate).getTime()
      const end = new Date(endDate).getTime()
      if (now < start) return '-敬请期待'
      if (now >= start && now <= end) return '-激战正酣'
      return '-完美闭幕'
    },

    /** 与 match/standings.vue 一致：按阶段类型拉积分或对阵 */
    refreshByRuleType() {
      this.list = []
      this.rounds = []
      if (!this.groupValue) return

      if (['循环赛', '双循环'].includes(this.ruleType)) {
        this.fetchTeamScores()
      } else if (this.ruleType === '淘汰赛') {
        this.fetchKnockout()
      } else {
        this.fetchTeamScores()
      }
    },

    fetchTeamScores() {
      this.bodyLoading = true
      getBMTeamScores1({
        matchId: this.groupValue,
        pageSize: 1000,
        stageId: this.stageValue,
      }).then(res => {
        const data = (res && res.data) || {}
        this.match = data.match || this.match
        const teamScores = data.teamScores || []
        const apiGroups = data.groups || []

        let groupName = ''
        const groupObject = {}
        for (let i = 0; i < teamScores.length; i++) {
          const score = teamScores[i]
          groupName = score.groupName
          if (groupObject[groupName]) {
            groupObject[groupName].push(score)
          } else {
            groupObject[groupName] = [score]
          }
        }

        const sportType = this.match.sportType
        const toRows = scores =>
          (scores || []).map((s, idx) => normalizeTeamScoreRow(s, idx))

        const groups = []
        if (apiGroups && apiGroups.length > 1) {
          const ordered = apiGroups.map(g => g.name)
          for (let j = 0; j < ordered.length; j++) {
            const name = ordered[j]
            groups.push({
              heading: name,
              sportType,
              value: toRows(groupObject[name]),
            })
          }
        } else {
          const rawVals = groupObject[groupName] || []
          groups.push({
            heading: groupName || '积分榜',
            sportType,
            value: toRows(rawVals),
          })
        }
        this.list = groups
      }).catch(() => {
        this.list = []
      }).finally(() => {
        this.bodyLoading = false
        this.$nextTick(() => this.updateBodyMinHeight())
      })
    },

    fetchKnockout() {
      this.bodyLoading = true
      getBMMatchStageGameList({
        matchId: this.groupValue,
        pageSize: 1000,
        stageId: this.stageValue,
      }).then(res => {
        const data = (res && res.data) || {}
        this.rounds = data.rounds || []
      }).catch(() => {
        this.rounds = []
      }).finally(() => {
        this.bodyLoading = false
        this.$nextTick(() => this.updateBodyMinHeight())
      })
    },
  },
}
</script>

<style scoped lang="scss">

.standings-page {
  min-height: 100vh;
  box-sizing: border-box;
  background: #fff;
  padding: 12px 16px 24px;
}

.standings-body {
  margin-top: 12px;
}

.state-tip {
  padding: 40px 16px;
  text-align: center;
}

.custom-tip {
  text-align: center;
  margin: 24px 0;
  font-size: 14px;
  color: #666;
}

.knockout-wrap {
  margin-bottom: 16px;
}

/* 分组名单独一行（白底） */
.group-block {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.match-note {
  font-size: 13px;
  color: #b5b5b5;
  padding: 16px 8px 8px;
  text-align: center;
}
</style>

<style lang="scss">
.van-sticky--fixed{
  padding: 12px 16px;
  background-color: #fff;
}
.standings-page {
  .van-dropdown-menu__bar {
    background-color: #ffffff;

    .van-dropdown-menu__item + .van-dropdown-menu__item {
      margin-left: 12px;
    }
    box-shadow: none;
  }

  .van-dropdown-menu__item {
    min-width: 0;
    justify-content: flex-start;
    border-radius: 5px;
    background-color: #f4f4f5;
  }

  .van-dropdown-menu__title {
    flex: 1;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding-right: 24px;

    .van-ellipsis {
      text-align: center;
      flex: 1;
      min-width: 0;
    }
  }

  .van-dropdown-menu__title::after {
    right: 10px;
    margin-top: -6px;
    border: 4px solid;
    border-color: transparent transparent #686b6f #686b6f;
  }

  .van-dropdown-menu__title--down::after {
    margin-top: -2px;
  }

  .van-dropdown-menu__title--active::after {
    border-color: transparent transparent currentColor currentColor !important;
  }
}
</style>
